import { Button, Stack, Typography } from '@mui/material';
import { useSession } from '@side/next-auth/react';
import { Dialog, DialogContent } from '@side/pantry';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { useGetOauthRequestUrl } from '@/common/hooks/useGetOauthRequestUrl';
import { navigateTo } from '@/utils/navigation';
import {
  ContactSyncAccountType,
  TrackContactSyncEvents,
  useTrackContactSyncAnalytics,
} from '$analytics/useTrackAnalytics/domains/contactSync';
import { contactsIntegrationModalOpen } from './ContactsIntegrationModal.state';
import { FollowUpBossIcon } from './icons/FollowUpBossIcon';
import { GoogleIcon } from './icons/GoogleIcon';

export const ContactsIntegrationModal = () => {
  const { data: session } = useSession();
  const [dialogOpen, setDialogOpen] = useAtom(contactsIntegrationModalOpen);
  const {
    getOauthRequestUrl: getFollowUpBossRequestUrl,
    loading: followUpBossRedirectUrlLoading,
  } = useGetOauthRequestUrl({
    provider: 'follow-up-boss',
    returnPath: window.location.pathname,
    accountId: session?.user?.id ?? '',
    accountType: 'user',
    errorMessage:
      'There was a problem connecting contacts. Please refresh and try again.',
  });
  const {
    getOauthRequestUrl: getGoogleRequestUrl,
    loading: googleRedirectUrlLoading,
  } = useGetOauthRequestUrl({
    provider: 'google',
    returnPath: window.location.pathname,
    accountId: session?.user?.id ?? '',
    accountType: 'user',
    errorMessage:
      'There was a problem connecting contacts. Please refresh and try again.',
  });
  const trackEvent = useTrackContactSyncAnalytics();

  const onConnectGoogleClick = useCallback(async () => {
    const requestUrl = await getGoogleRequestUrl();
    if (!requestUrl) {
      return;
    }
    trackEvent(TrackContactSyncEvents.ClickConnectButton, {
      eventContainer: 'connect_contact_modal',
      accountType: ContactSyncAccountType.Google,
    });
    navigateTo(requestUrl);
  }, [getGoogleRequestUrl, trackEvent]);

  const onConnectFollowUpBossClick = useCallback(async () => {
    const requestUrl = await getFollowUpBossRequestUrl();
    if (!requestUrl) {
      return;
    }
    trackEvent(TrackContactSyncEvents.ClickConnectButton, {
      eventContainer: 'connect_contact_modal',
      accountType: ContactSyncAccountType.FollowUpBoss,
    });
    navigateTo(requestUrl);
  }, [getFollowUpBossRequestUrl, trackEvent]);

  if (!session?.user.id) {
    return null;
  }

  return (
    <Dialog
      open={dialogOpen}
      onClose={() => {
        setDialogOpen(false);
        trackEvent(TrackContactSyncEvents.UnmountModal, {
          eventTarget: 'background',
        });
      }}
      maxWidth="sm"
    >
      <DialogContent
        title="Choose Account to Connect"
        onClose={() => {
          setDialogOpen(false);
          trackEvent(TrackContactSyncEvents.UnmountModal, {
            eventTarget: 'close_button',
          });
        }}
      >
        <Stack direction="column" spacing={3}>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Add an existing account to access saved contacts within your
            transactions
          </Typography>

          <Button
            variant="outlined"
            color="utility"
            loading={googleRedirectUrlLoading}
            disabled={googleRedirectUrlLoading}
            onClick={() => void onConnectGoogleClick()}
            startIcon={<GoogleIcon />}
            fullWidth
          >
            Connect Google Contacts
          </Button>

          <Button
            variant="outlined"
            color="utility"
            loading={followUpBossRedirectUrlLoading}
            disabled={followUpBossRedirectUrlLoading}
            onClick={() => void onConnectFollowUpBossClick()}
            startIcon={<FollowUpBossIcon />}
            fullWidth
          >
            Connect Follow Up Boss Contacts
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
